import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { todolistsService } from '@/services/collections/todolists';
import { useAuth } from '@/contexts/AuthContext';
import type {
  TodoList,
  TodoItem,
  TaskPriority,
  CreateTodoItemInput,
  UpdateTodoItemInput,
  CreateSubTaskInput,
  InviteUserInput,
} from '@/types/todolist';
import type { SharePermission } from '@/types/thinkspace';
import type { RecurrenceRule } from '@/types/recurrence';

export const useTodoList = (id: string) => {
  const { user } = useAuth();
  const [todoList, setTodoList] = useState<TodoList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const pendingUpdates = useRef<TodoItem[] | null>(null);
  const updateTimeout = useRef<NodeJS.Timeout | null>(null);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    const unsubscribe = todolistsService.subscribe(
      id,
      (list) => {
        setTodoList(list);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [id]);

  // Check access permissions
  const access = todoList && user
    ? todolistsService.checkAccess(todoList, user.uid, user.email || '')
    : { hasAccess: false, canEdit: false, isOwner: false };

  // Flush pending updates on unmount
  useEffect(() => {
    return () => {
      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
      }
      const pending = pendingUpdates.current;
      if (pending && id) {
        todolistsService.updateItems(id, pending).catch(console.error);
      }
    };
  }, [id]);

  // Debounced save to Firestore
  const saveItems = useCallback(
    async (items: TodoItem[]) => {
      if (!id || !access.canEdit) return;

      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
      }

      pendingUpdates.current = items;

      updateTimeout.current = setTimeout(async () => {
        try {
          const pending = pendingUpdates.current;
          if (pending) {
            await todolistsService.updateItems(id, pending);
            pendingUpdates.current = null;
          }
        } catch (err) {
          console.error('Failed to save items:', err);
        }
      }, 300);
    },
    [id, access.canEdit]
  );

  // Update list metadata
  const updateList = useCallback(
    async (data: { name?: string; description?: string }) => {
      if (!id || !access.canEdit) return;
      await todolistsService.update(id, data);
    },
    [id, access.canEdit]
  );

  // Add a new todo item
  const addItem = useCallback(
    async (
      title: string,
      dueDate?: Date | null,
      priority?: TaskPriority | null,
      recurrence?: RecurrenceRule | null
    ) => {
      if (!id || !access.canEdit) return null;
      const input: CreateTodoItemInput = { title };
      if (dueDate) input.dueDate = dueDate;
      if (priority) input.priority = priority;
      if (recurrence) input.recurrence = recurrence;
      return todolistsService.addItem(id, input);
    },
    [id, access.canEdit]
  );

  // Update a todo item
  const updateItem = useCallback(
    async (itemId: string, updates: UpdateTodoItemInput) => {
      if (!id || !access.canEdit) return;
      await todolistsService.updateItem(id, itemId, updates);
    },
    [id, access.canEdit]
  );

  // Toggle item completion
  const toggleItem = useCallback(
    async (itemId: string) => {
      if (!id || !access.canEdit || !todoList) return;

      const item = todoList.items.find((i) => i.id === itemId);
      if (!item) return;

      // If completing a recurring task, use special completion logic
      if (!item.completed && item.isRecurring) {
        await todolistsService.completeRecurringItem(id, itemId);
      } else {
        await todolistsService.updateItem(id, itemId, {
          completed: !item.completed,
        });
      }
    },
    [id, access.canEdit, todoList]
  );

  // Delete a todo item
  const deleteItem = useCallback(
    async (itemId: string) => {
      if (!id || !access.canEdit) return;
      await todolistsService.deleteItem(id, itemId);
    },
    [id, access.canEdit]
  );

  // Reorder items (after drag-and-drop)
  const reorderItems = useCallback(
    async (itemIds: string[]) => {
      if (!id || !access.canEdit) return;
      await todolistsService.reorderItems(id, itemIds);
    },
    [id, access.canEdit]
  );

  // Add a subtask
  const addSubtask = useCallback(
    async (itemId: string, input: CreateSubTaskInput) => {
      if (!id || !access.canEdit) return null;
      return todolistsService.addSubtask(id, itemId, input);
    },
    [id, access.canEdit]
  );

  // Toggle subtask completion
  const toggleSubtask = useCallback(
    async (itemId: string, subtaskId: string) => {
      if (!id || !access.canEdit) return;
      await todolistsService.toggleSubtask(id, itemId, subtaskId);
    },
    [id, access.canEdit]
  );

  // Delete a subtask
  const deleteSubtask = useCallback(
    async (itemId: string, subtaskId: string) => {
      if (!id || !access.canEdit) return;
      await todolistsService.deleteSubtask(id, itemId, subtaskId);
    },
    [id, access.canEdit]
  );

  return {
    todoList,
    loading,
    error,
    // Return both access object and convenience properties
    access,
    canEdit: access.canEdit,
    isOwner: access.isOwner,
    updateList,
    addItem,
    updateItem,
    toggleItem,
    deleteItem,
    reorderItems,
    addSubtask,
    toggleSubtask,
    deleteSubtask,
    saveItems,
  };
};

// Sharing mutations
export const useInviteToTodoList = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      todoListId,
      input,
    }: {
      todoListId: string;
      input: InviteUserInput;
    }) => todolistsService.inviteUser(todoListId, input, user!.uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists'] });
    },
  });
};

export const useUpdateTodoListPermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      todoListId,
      userEmail,
      permission,
    }: {
      todoListId: string;
      userEmail: string;
      permission: SharePermission;
    }) => todolistsService.updatePermission(todoListId, userEmail, permission),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists'] });
    },
  });
};

export const useRemoveTodoListSharedUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      todoListId,
      userEmail,
    }: {
      todoListId: string;
      userEmail: string;
    }) => todolistsService.removeSharedUser(todoListId, userEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists'] });
    },
  });
};
